import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authFetch } from '../../Utilities/Api';
import { useChatContext } from '../../context/ChatContext';
import './NotificationsDropdown.css';

const NotificationsDropdown = ({ onClose }) => {
    const navigate = useNavigate();
    const { openChat } = useChatContext();

    const [notifications, setNotifications] = useState([]);
    const [unreadCount, setUnreadCount] = useState(0);

    // 1. Fetch Notifications
    useEffect(() => {
        const fetchNotifications = async () => {
            try {
                const res = await authFetch('/notifications');
                const data = await res.json();
                if (res.ok) {
                    const list = Array.isArray(data.notifications) ? data.notifications : (Array.isArray(data) ? data : []);
                    setNotifications(list);
                    setUnreadCount(data.unreadCount || 0);
                }
            } catch (err) {
                console.error("❌ [Notifications] Fetch Failed:", err);
            }
        };
        fetchNotifications();
    }, []);

    // ✅ NEW: Handle Clear All
    const handleClearAll = async (e) => {
        e.stopPropagation();
        if (!window.confirm("Delete all notifications permanently?")) return;

        try {
            const res = await authFetch('/notifications/clear-all', { method: 'DELETE' });
            if (res.ok) {
                setNotifications([]);
                setUnreadCount(0);
            }
        } catch (err) {
            console.error("❌ Failed to clear notifications:", err);
        }
    };

    // Handle Individual Deletion
    const handleDeleteNotification = async (e, noteId) => {
        e.stopPropagation();
        try {
            const res = await authFetch(`/notifications/${noteId}`, { method: 'DELETE' });
            if (res.ok) {
                const deletedNote = notifications.find(n => n._id === noteId);
                setNotifications(prev => prev.filter(n => n._id !== noteId));
                if (deletedNote && !deletedNote.isRead) {
                    setUnreadCount(prev => Math.max(0, prev - 1));
                }
            }
        } catch (err) {
            console.error("❌ Failed to delete notification:", err);
        }
    };

    const handleNotificationClick = async (note) => {
        if (!note.isRead) {
            authFetch(`/notifications/${note._id}/read`, { method: 'PUT' });
            setNotifications(prev => prev.map(n =>
                n._id === note._id ? { ...n, isRead: true } : n
            ));
            setUnreadCount(prev => Math.max(0, prev - 1));
        }

        onClose();

        if (note.type === 'NEW_MESSAGE' || note.type === 'CHAT') {
            const chatId = note.relatedId || (note.data && note.data.chatId);
            if (chatId) openChat(chatId);
        }
        else if (note.type === 'FRIEND_REQUEST' || note.type === 'FRIEND_ACCEPT') {
            const targetUser = note.sender?.username || note.relatedId;
            if (targetUser) navigate(`/user/${targetUser}`);
        }
        else {
            if (note.sender?.username) navigate(`/user/${note.sender.username}`);
        }
    };

    const handleMarkAllRead = async (e) => {
        e.stopPropagation();
        try {
            await authFetch('/notifications/read-all', { method: 'PUT' });
            setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
            setUnreadCount(0);
        } catch (err) {
            console.error("Failed to mark all as read", err);
        }
    };

    return (
        <div className="notifications-dropdown">
            <div className="dropdown-header">
                <strong>
                    Notifications
                    {unreadCount > 0 && <span className="count-text">({unreadCount})</span>}
                </strong>
                <div className="header-actions">
                    <button className="action-link" onClick={handleMarkAllRead}>
                        Mark read
                    </button>
                    <span className="divider">|</span>
                    <button className="action-link clear-btn" onClick={handleClearAll}>
                        Clear all
                    </button>
                </div>
            </div>

            <div className="notification-list">
                {!notifications || notifications.length === 0 ? (
                    <div className="empty-note">No notifications yet</div>
                ) : (
                    notifications.map((note) => {
                        const senderName = note.sender?.nickname || note.sender?.username || "Someone";
                        const rawMessage = note.message || "";
                        const displayMessage = typeof rawMessage === 'string' ? rawMessage.replace(senderName, "").trim() : "";

                        return (
                            <div
                                key={note._id}
                                className={`notification-item ${!note.isRead ? 'unread' : ''}`}
                                onClick={() => handleNotificationClick(note)}
                            >
                                <img
                                    src={note.sender?.profilePicture || "https://via.placeholder.com/40"}
                                    alt="Avatar"
                                    className="note-avatar"
                                />
                                <div className="note-content">
                                    <p className="note-text">
                                        <span className="note-user">{senderName}</span> {displayMessage}
                                    </p>
                                    <span className="note-time">
                                        {new Date(note.createdAt).toLocaleDateString()}
                                    </span>
                                </div>

                                <button
                                    className="delete-note-btn"
                                    onClick={(e) => handleDeleteNotification(e, note._id)}
                                    aria-label="Delete notification"
                                >
                                    &times;
                                </button>

                                {!note.isRead && <div className="unread-dot"></div>}
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
};

export default NotificationsDropdown;